import { useNode } from "@craftjs/core";
import React from "react";

import {
  ColorInput,
  UnitInput,
  PaddingInput,
  Tabs,
  Spacer,
  MarginInput,
  AlignInput,
  NumberInput,
  BorderRadiusInput,
  BorderInput,
  Panel,
  TextInput,
  BoxShadowInput,
} from "../../input";
import { isUpdatedFromDefault } from "../../utils";

import { supportLibrary } from "../../block-supports";
import { INPUT_LIMITS } from "../../input-limits";

export const FormRangeSettings = () => {
  const {
    id,
    actions: { setProp },
    props,
    dom,
    supports,
  } = useNode((node) => ({
    id: node.id,
    text: node.data.props.text,
    props: node.data.props,
    dom: node.dom,
    supports: node.data?.type?.craft?.related?.supports,
  }));

  const {
    width,
    height,
    align,
    alignTablet,
    alignMobile,
    layoutMargin,
    containerBackgroundColor,
    layoutMarginTablet,
    layoutMarginMobile,
    layoutPadding,
    layoutPaddingTablet,
    layoutPaddingMobile,
    layoutBorder,
    layoutBorderRadius,
    layoutBoxShadow,
    sliderThumbColor,
    sliderThumbWidth,
    sliderThumbHeight,
    sliderThumbBorder,
    sliderThumbBorderRadius,
    sliderTrackColor,
    sliderTrackWidth,
    sliderTrackHeight,
    sliderTrackBorder,
    sliderTrackBorderRadius,
    min,
    max,
    step,
    value,
    name,
  } = props;

  function GeneralSettings() {
    return (
      <div className="g-settings__wrapper">
        <Spacer spacing={"1rem"}>
          <TextInput
            minLength={INPUT_LIMITS["form-input-name"].minlength}
            maxLength={INPUT_LIMITS["form-input-name"].maxlength}
            resetValue={undefined}
            value={name}
            onChange={(val) => {
              setProp((props) => {
                props.name = val;
              });
            }}
            label={"Name"}
          />
          <NumberInput
            resetValue={undefined}
            value={min}
            onChange={(val) => {
              setProp((props) => {
                props.min = val;
              });
            }}
            label={"Min"}
            min={INPUT_LIMITS["form-range"].min}
          />
          <NumberInput
            resetValue={undefined}
            value={max}
            onChange={(val) => {
              setProp((props) => {
                props.max = val;
              });
            }}
            label={"Max"}
            max={INPUT_LIMITS["form-range"].max}
          />
          <NumberInput
            resetValue={undefined}
            value={step}
            onChange={(val) => {
              setProp((props) => {
                props.step = val;
              });
            }}
            label={"Step"}
            step={INPUT_LIMITS["form-range"].step}
          />
          <NumberInput
            resetValue={undefined}
            value={value}
            onChange={(val) => {
              setProp((props) => {
                props.value = val;
              });
            }}
            max={INPUT_LIMITS["form-range"].max}
            min={INPUT_LIMITS["form-range"].min}
            step={INPUT_LIMITS["form-range"].step}
            label={"Value"}
          />
          <UnitInput
            resetValue={undefined}
            value={width}
            onChange={(val) => {
              setProp((props) => {
                props.width = val;
              });
            }}
            label={"Width"}
          />
          <UnitInput
            resetValue={undefined}
            value={height}
            onChange={(val) => {
              setProp((props) => {
                props.height = val;
              });
            }}
            label={"Height"}
          />
          <AlignInput
            isResponsive={true}
            resetValue={undefined}
            valueTablet={alignTablet}
            valueMobile={alignMobile}
            value={align}
            onChange={(val) => {
              setProp((props) => {
                props.align = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.alignTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.alignMobile = val;
              });
            }}
            label={"Align"}
          />
        </Spacer>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: sliderThumbWidth },
            { key: sliderThumbHeight },
            { key: sliderTrackWidth },
            { key: sliderTrackHeight },
          ])}
          id={`block-setting-${id}-slider`}
          title={"Slider"}
        >
          <UnitInput
            value={sliderThumbWidth}
            onChange={(val) => {
              setProp((props) => {
                props.sliderThumbWidth = val;
              });
            }}
            label={"Thumb Width"}
          />
          <UnitInput
            value={sliderThumbHeight}
            onChange={(val) => {
              setProp((props) => {
                props.sliderThumbHeight = val;
              });
            }}
            label={"Thumb Height"}
          />{" "}
          <UnitInput
            value={sliderTrackWidth}
            onChange={(val) => {
              setProp((props) => {
                props.sliderTrackWidth = val;
              });
            }}
            label={"Track Width"}
          />
          <UnitInput
            value={sliderTrackHeight}
            onChange={(val) => {
              setProp((props) => {
                props.sliderTrackHeight = val;
              });
            }}
            label={"Track Height"}
          />
        </Panel>
      </div>
    );
  }

  function StyleSettings() {
    return (
      <>
        <div className="g-settings__wrapper">
          <Spacer spacing={"1rem"}>
            <ColorInput
              value={sliderThumbColor}
              onChange={(val) => {
                setProp((props) => {
                  props.sliderThumbColor = val;
                });
              }}
              label={"Thumb"}
            />
            <ColorInput
              value={sliderTrackColor}
              onChange={(val) => {
                setProp((props) => {
                  props.sliderTrackColor = val;
                });
              }}
              label={"Track"}
            />
          </Spacer>
          <Panel
            isUpdated={isUpdatedFromDefault([
              { key: containerBackgroundColor },
            ])}
            id={`block-setting-${id}-container`}
            title={"Container"}
            initialOpen={false}
          >
            <ColorInput
              value={containerBackgroundColor}
              onChange={(val) => {
                setProp((props) => {
                  props.containerBackgroundColor = val;
                });
              }}
              label={"Background"}
            />
          </Panel>
        </div>
      </>
    );
  }

  function AdvancedSettings() {
    return (
      <>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: layoutMarginTablet },
            { key: layoutMarginMobile },
            { key: layoutMargin },
            { key: layoutPaddingTablet },
            { key: layoutPaddingMobile },
            { key: layoutPadding },
          ])}
          id={`block-setting-${id}-slider-layout`}
          title={"Slider Layout"}
        >
          <MarginInput
            label={"Margin"}
            isResponsive={true}
            valueTablet={layoutMarginTablet}
            valueMobile={layoutMarginMobile}
            value={layoutMargin}
            onChange={(val) => {
              setProp((props) => {
                props.layoutMargin = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.layoutMarginTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.layoutMarginMobile = val;
              });
            }}
          />
          <PaddingInput
            label={"Padding"}
            isResponsive={true}
            valueTablet={layoutPaddingTablet}
            valueMobile={layoutPaddingMobile}
            value={layoutPadding}
            onChange={(val) => {
              setProp((props) => {
                props.layoutPadding = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.layoutPaddingTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.layoutPaddingMobile = val;
              });
            }}
          />
        </Panel>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: layoutBorder },
            { key: layoutBorderRadius },
          ])}
          id={`block-setting-${id}-slider-border`}
          title={"Slider Border"}
        >
          <BorderInput
            resetValue={undefined}
            value={layoutBorder}
            onChange={(val) => {
              setProp((props) => {
                props.layoutBorder = val;
              });
            }}
            label={"Border"}
          />
          <BorderRadiusInput
            resetValue={undefined}
            value={layoutBorderRadius}
            onChange={(val) => {
              setProp((props) => {
                props.layoutBorderRadius = val;
              });
            }}
            label={"Border Radius"}
          />
        </Panel>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: sliderThumbBorder },
            { key: sliderThumbBorderRadius },
            { key: sliderTrackBorder },
            { key: sliderTrackBorderRadius },
          ])}
          id={`block-setting-${id}-slider-border`}
          title={"Borders"}
        >
          <BorderInput
            resetValue={undefined}
            value={sliderThumbBorder}
            onChange={(val) => {
              setProp((props) => {
                props.sliderThumbBorder = val;
              });
            }}
            label={"Thumb"}
          />
          <BorderRadiusInput
            resetValue={undefined}
            value={sliderThumbBorderRadius}
            onChange={(val) => {
              setProp((props) => {
                props.sliderThumbBorderRadius = val;
              });
            }}
            label={"Thumb Border Radius"}
          />
          <BorderInput
            resetValue={undefined}
            value={sliderTrackBorder}
            onChange={(val) => {
              setProp((props) => {
                props.sliderTrackBorder = val;
              });
            }}
            label={"Track "}
          />
          <BorderRadiusInput
            resetValue={undefined}
            value={sliderTrackBorderRadius}
            onChange={(val) => {
              setProp((props) => {
                props.sliderTrackBorderRadius = val;
              });
            }}
            label={"Track Border Radius"}
          />
        </Panel>
        <Panel
          isUpdated={isUpdatedFromDefault([{ key: layoutBoxShadow }])}
          id={`block-setting-${id}-slider-shadows`}
          title={"Slider Shadows"}
        >
          <BoxShadowInput
            resetValue={undefined}
            value={layoutBoxShadow}
            onChange={(val) => {
              setProp((props) => {
                props.layoutBoxShadow = val;
              });
            }}
          />
        </Panel>

        {supports &&
          supports.map((support) => {
            var Component = supportLibrary[support]?.component;

            if (!Component) {
              return;
            }

            return (
              <>
                <Component
                  block={{
                    id,
                    props,
                    dom,
                  }}
                  actions={{
                    setProp,
                  }}
                ></Component>
              </>
            );
          })}
      </>
    );
  }
  const tabs = [
    {
      label: "General",
      key: "general",
      children: GeneralSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: name },
        { key: min },
        { key: max },
        { key: step },
        { key: value },
        { key: width },
        { key: height },
        { key: alignTablet },
        { key: alignMobile },
        { key: align },
        { key: sliderThumbWidth },
        { key: sliderThumbHeight },
        { key: sliderTrackWidth },
        { key: sliderTrackHeight },
      ]),
    },
    {
      label: "Style",
      key: "style",
      children: StyleSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: sliderThumbColor },
        { key: containerBackgroundColor },
        { key: sliderTrackColor },
      ]),
    },
    {
      label: "Advanced",
      key: "advanced",
      children: AdvancedSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: layoutMarginTablet },
        { key: layoutMarginMobile },
        { key: layoutMargin },
        { key: layoutPaddingTablet },
        { key: layoutPaddingMobile },
        { key: layoutPadding },
        { key: layoutBorder },
        { key: layoutBorderRadius },
        { key: layoutBoxShadow },
        { key: sliderThumbBorder },
        { key: sliderThumbBorderRadius },
        { key: sliderTrackBorder },
        { key: sliderTrackBorderRadius },
      ]),
    },
  ];

  return (
    <div>
      <Tabs
        type="block-setting"
        id={`block-settings-tab-${id}`}
        items={tabs.map((item, i) => {
          return {
            label: item.label,
            key: item.key,
            children: item.children,
          };
        })}
      />
    </div>
  );
};
